import React from "react";
import RoundTripListItem from "./RoundTripListItem";
import { getImageUrl } from "../../utils/GetImageUrl";
import { RoundTripFlightMatch } from "../../models/RoundTripFlightMatch";

interface Props {
  flightList: RoundTripFlightMatch[];
  groupByDestination?: boolean;
}

const renderFlightListItem = (flight: RoundTripFlightMatch, index: number) => {
  const IMAGE_URL = getImageUrl(
    flight.round_trip_flight.outbound_flight.destination_iata
  );

  return (
    <RoundTripListItem
      key={`${flight.round_trip_flight.outbound_flight.destination_iata}-${flight.round_trip_flight.outbound_flight.departure_date}-${index}`}
      roundTripFlightMatch={flight}
      imageUrl={IMAGE_URL}
    />
  );
};

const RoundTripList: React.FC<Props> = ({ flightList, groupByDestination = false }) => {
  if (!groupByDestination) {
    return (
      <div className="space-y-5">
        {flightList.map(renderFlightListItem)}
      </div>
    );
  }

  const groupedFlights = flightList.reduce<Record<string, RoundTripFlightMatch[]>>(
    (groups, flight) => {
      const destinationIata = flight.round_trip_flight.outbound_flight.destination_iata;
      if (!groups[destinationIata]) {
        groups[destinationIata] = [];
      }
      groups[destinationIata].push(flight);
      return groups;
    },
    {}
  );

  const destinationGroups = Object.entries(groupedFlights)
    .map(([destinationIata, flights]) => ({
      destinationIata,
      destinationName: flights[0].round_trip_flight.destination_city,
      flights: [...flights].sort(
        (a, b) => a.round_trip_flight.total_price - b.round_trip_flight.total_price
      ),
      cheapestPrice: Math.min(...flights.map((flight) => flight.round_trip_flight.total_price)),
    }))
    .sort((a, b) => {
      const priceDiff = a.cheapestPrice - b.cheapestPrice;
      if (priceDiff !== 0) return priceDiff;
      return a.destinationName.localeCompare(b.destinationName);
    });

  return (
    <div className="space-y-8">
      {destinationGroups.map((group) => (
        <section key={group.destinationIata} className="flex flex-col gap-3">
          <div className="flex items-end justify-between px-1">
            <div className="flex items-baseline gap-3">
              <h3 className="text-lg font-extrabold tracking-tight text-[#2D2D2D]">
                {group.destinationName}
              </h3>
              <span className="text-xs font-bold text-[#BEBEBE]">
                {group.destinationIata}
              </span>
            </div>
            <span className="text-xs font-bold text-[#BEBEBE]">
              ab {group.cheapestPrice.toFixed(0)}
              {group.flights[0].round_trip_flight.outbound_flight.currency === "EUR"
                ? "€"
                : ` ${group.flights[0].round_trip_flight.outbound_flight.currency}`}
            </span>
          </div>
          <div className="space-y-5">
            {group.flights.map(renderFlightListItem)}
          </div>
        </section>
      ))}
    </div>
  );
};

export default RoundTripList;
